import axios from "axios"; // Import axios
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next"; // Import translation hook
import { AiFillEye, AiFillEyeInvisible } from "react-icons/ai";
import { useNavigate } from "react-router-dom";

const ForgetPassword = () => {
  const { t } = useTranslation(); // Use translation hook

  const navigate = useNavigate();

  // Phone + SMS flow states
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [smsSending, setSmsSending] = useState(false);
  const [smsVerified, setSmsVerified] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [captchaImg, setCaptchaImg] = useState("");
  const [captchaToken, setCaptchaToken] = useState("");
  const [captchaText, setCaptchaText] = useState("");
  const [currentStep, setCurrentStep] = useState(1); // 1: verify, 2: reset password
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [resetLoading, setResetLoading] = useState(false);
  const [passwordStatus, setPasswordStatus] = useState("");
  const [formErrors, setFormErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  // No separate error/success state; use toasts for feedback

  // Format HK phone for display: prefer "+852 XXXXXXXX"
  const formatHKPhone = (raw) => {
    if (!raw) return "";
    const digits = String(raw).replace(/\D/g, "");
    if (/^\d{8}$/.test(digits)) return `+852 ${digits}`;
    if (/^852\d{8}$/.test(digits)) return `+852 ${digits.slice(3)}`;
    return raw;
  };

  // Validate password complexity similar to Register
  const validatePasswordFormat = (password) => {
    const minLength = 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecialChars = /[!@#$%^&*]/.test(password);
    const typesCount = [
      hasUpperCase,
      hasLowerCase,
      hasNumbers,
      hasSpecialChars,
    ].filter(Boolean).length;
    if (password.length < minLength) {
      setPasswordStatus(t("passwordInvalidLength"));
    } else if (typesCount < 2) {
      setPasswordStatus(t("passwordInvalidComplexity"));
    } else {
      setPasswordStatus("");
    }
  };

  const getInputClass = (field) => {
    return formErrors[field] ? "input-error" : "input-default";
  };

  const getCaptcha = async () => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/captcha/generate.php`,
        {
          method: "GET",
          credentials: "include",
        }
      );
      const json = await res.json();
      if (json.success) {
        setCaptchaImg(json.image);
        setCaptchaToken(json.token);
      } else {
        toast.error(
          json.message ||
            t("captchaLoadFailed", { defaultValue: "Failed to load CAPTCHA" })
        );
      }
    } catch (e) {
      toast.error(t("errorMessage"));
    }
  };

  useEffect(() => {
    getCaptcha();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setInterval(() => setCooldown((c) => (c > 0 ? c - 1 : 0)), 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  const sendSmsCode = async () => {
    // validate HK phone: 8 digits or with +852 prefix
    if (
      !phone ||
      (!/^\+?852[- ]?\d{8}$/.test(phone) && !/^\d{8}$/.test(phone))
    ) {
      toast.error(
        t("invalidHKPhone", { defaultValue: "Invalid Hong Kong phone" })
      );
      setFormErrors((p) => ({
        ...p,
        phone: t("invalidHKPhone", { defaultValue: "Invalid Hong Kong phone" }),
      }));
      return;
    }
    if (!captchaToken) {
      toast.error(
        t("captchaRequired", { defaultValue: "Please complete CAPTCHA" })
      );
      setFormErrors((p) => ({
        ...p,
        captcha: t("captchaRequired", {
          defaultValue: "Please complete CAPTCHA",
        }),
      }));
      return;
    }
    if (!captchaText) {
      toast.error(
        t("enterCaptcha", { defaultValue: "Please enter CAPTCHA text" })
      );
      setFormErrors((p) => ({
        ...p,
        captcha: t("enterCaptcha", {
          defaultValue: "Please enter CAPTCHA text",
        }),
      }));
      return;
    }
    if (cooldown > 0) {
      toast.error(
        t("waitBeforeResend", {
          defaultValue: "Please wait before resending code",
        })
      );
      return;
    }
    setSmsSending(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/sms/send_code.php`,
        {
          phone,
          context: "reset",
          captchaToken,
          captcha: captchaText,
        },
        { withCredentials: true }
      );
      const data = res.data;
      if (!data.success) {
        throw new Error(data.message || "Failed to send code");
      }
      toast.success(t("codeSent"));
      setCooldown(60);
      setCaptchaText("");
      await getCaptcha();
    } catch (e) {
      // If backend includes retryAfter
      const retryAfter = e?.response?.data?.retryAfter;
      if (retryAfter) setCooldown(Number(retryAfter));
      toast.error(t("errorMessage"));
    } finally {
      setSmsSending(false);
    }
  };

  const verifySmsCode = async () => {
    if (!phone || !code) {
      toast.error(
        t("phoneAndCodeRequired", {
          defaultValue: "Phone and code are required",
        })
      );
      return;
    }
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/sms/verify_code.php`,
        { phone, code },
        { withCredentials: true }
      );
      const data = res.data;
      if (!data.success) throw new Error(data.message || "Verify failed");
      setSmsVerified(true);
      toast.success(t("verified", { defaultValue: "Verified" }));
      // Move to Step 2: reset password in-page
      setCurrentStep(2);
    } catch (e) {
      setSmsVerified(false);
      toast.error(t("errorMessage"));
    }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    const errors = {};
    let valid = true;
    if (!smsVerified) {
      errors.code = t("verifyPhoneToContinue", {
        defaultValue: "Please verify your phone number",
      });
      valid = false;
    }
    if (!newPassword) {
      errors.newPassword = t("passwordRequired");
      valid = false;
    }
    if (!confirmPassword) {
      errors.confirmPassword = t("passwordRequired");
      valid = false;
    }
    validatePasswordFormat(newPassword);
    if (passwordStatus) {
      errors.newPassword = passwordStatus;
      valid = false;
    }
    if (newPassword && confirmPassword && newPassword !== confirmPassword) {
      errors.confirmPassword = t("resetPassword.errorMessage.passwordMismatch");
      valid = false;
    }
    setFormErrors(errors);
    if (!valid) {
      toast.error(Object.values(errors).join(" ") || t("formInvalid"));
      return;
    }
    setResetLoading(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/reset_password_with_sms.php`,
        { phone, code, new_password: newPassword },
        { withCredentials: true }
      );
      toast.success(t("resetPassword.successMessage"));
      // Clear sensitive fields
      setNewPassword("");
      setConfirmPassword("");
      // Optionally redirect to login page after a delay
      setTimeout(() => {
         navigate("/login");
      }, 1000);
    } catch (err) {
      const msg = err?.response?.data?.message || err.message;
      if (msg === "invalidCode") {
        toast.error(t("resetPassword.invalidCode"));
      } else if (msg === "codeExpired") {
        toast.error(t("resetPassword.codeExpired"));
      } else if (msg === "User not found") {
        toast.error(t("errorMessage"));
      } else {
        toast.error(t("errorMessage"));
      }
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div className="form-container">
      <h2>{t("forgetPassword.title")}</h2>
      <form onSubmit={(e) => e.preventDefault()}>
        {/* Step indicator */}
        <div className="text-sm text-gray-600 mb-2">
          {t("stepIndicator", {
            currentStep,
            defaultValue: `Step ${currentStep} / 2`,
          })}
        </div>
        {currentStep === 1 && (
          <>
            {/* Phone input */}
            <div>
              <label htmlFor="phone">
                {t("phoneHK", { defaultValue: "Phone (HK)" })}:
              </label>
              <div className="flex gap-2 items-center">
                <input
                  type="tel"
                  id="phone"
                  value={phone}
                  onChange={(e) => {
                    setPhone(e.target.value);
                    if (smsVerified) setSmsVerified(false);
                  }}
                  placeholder={t("phonePlaceholder", {
                    defaultValue: "92345678",
                  })}
                  required
                />
              </div>
            </div>

            {/* CAPTCHA */}
            <div className="flex items-center gap-2 mt-3">
              {captchaImg && (
                <img
                  src={captchaImg}
                  alt="captcha"
                  className="h-12 border rounded"
                  onClick={getCaptcha}
                  title={t("refresh", { defaultValue: "Refresh" })}
                />
              )}
              <input
                type="text"
                name="captcha"
                placeholder={t("enterCaptcha", {
                  defaultValue: "Enter CAPTCHA",
                })}
                className="input-default"
                value={captchaText}
                onChange={(e) => setCaptchaText(e.target.value)}
              />
              <button type="button" onClick={getCaptcha}>
                {t("refresh", { defaultValue: "Refresh" })}
              </button>
            </div>
            <div className="mt-3 ">
              <button
                type="button"
                disabled={smsSending || cooldown > 0}
                onClick={sendSmsCode}
              >
                {cooldown > 0
                  ? t("resendIn", {
                      seconds: cooldown,
                      defaultValue: `Resend in ${cooldown}s`,
                    })
                  : smsSending
                  ? t("sending")
                  : t("sendCode")}
              </button>
            </div>

            {/* Code verify */}
            <div className="mt-3">
              <label htmlFor="code">{t("smsCode")}:</label>
              <div className="flex gap-2 items-center">
                <input
                  type="text"
                  id="code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  required
                />
                <button type="button" onClick={verifySmsCode}>
                  {t("verify", { defaultValue: "Verify" })}
                </button>
                {smsVerified && (
                  <span className="text-green-600 text-sm" aria-live="polite">
                    {t("verified", { defaultValue: "Verified" })}
                  </span>
                )}
              </div>
            </div>
          </>
        )}

        {currentStep === 2 && (
          <>
            <div className="text-sm text-green-700 mb-2">
              {t("phoneVerified", { defaultValue: "Phone verified" })}:{" "}
              {formatHKPhone(phone)}
            </div>
            <div>
              <label>{t("resetPassword.newPassword")}</label>
              <div className="relative">
                <div
                  className="cursor-pointer absolute right-2 top-2"
                  onClick={() => setShowPassword((s) => !s)}
                >
                  {showPassword ? (
                    <AiFillEyeInvisible size={20} />
                  ) : (
                    <AiFillEye size={20} />
                  )}
                </div>
                <input
                  type={showPassword ? "text" : "password"}
                  className={getInputClass("newPassword")}
                  value={newPassword}
                  onChange={(e) => {
                    setNewPassword(e.target.value);
                    if (formErrors.newPassword)
                      setFormErrors((p) => ({ ...p, newPassword: "" }));
                    validatePasswordFormat(e.target.value);
                  }}
                  required
                />
              </div>
              {passwordStatus && (
                <div className="error-message">{passwordStatus}</div>
              )}
            </div>
            <div>
              <label>{t("resetPassword.confirmPassword")}</label>
              <div className="relative">
                <div
                  className="cursor-pointer absolute right-2 top-2"
                  onClick={() => setShowConfirmPassword((s) => !s)}
                >
                  {showConfirmPassword ? (
                    <AiFillEyeInvisible size={20} />
                  ) : (
                    <AiFillEye size={20} />
                  )}
                </div>
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  className={getInputClass("confirmPassword")}
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    if (formErrors.confirmPassword)
                      setFormErrors((p) => ({ ...p, confirmPassword: "" }));
                  }}
                  required
                />
              </div>
              {formErrors.confirmPassword && (
                <div className="error-message">
                  {formErrors.confirmPassword}
                </div>
              )}
            </div>
            <div className="p-4">
              <button
                type="button"
                onClick={submitReset}
                disabled={resetLoading}
              >
                {resetLoading
                  ? t("register.loading")
                  : t("resetPassword.submit")}
              </button>
            </div>
          </>
        )}
      </form>
    </div>
  );
};

export default ForgetPassword;
